import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, FileText, Search, Mic, Cake, Sparkles, PartyPopper, Home, Paperclip, Bot } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { ChatAttachment } from "../components/ChatAttachment";
import { PreferencesDiffCard } from "../components/PreferencesDiffCard";
import { bumpUnreadMessages } from "../lib/unreadMessages";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateTime } from "../lib/dateFormat";

const MAX_ATTACHMENTS = 3;
const ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt";

// Feb 2026 · Directive · Nachrichten-Verlauf-Filter (Admin-Audit).
// Filter-Chips im Kunden-Thread, damit Admins gezielt prüfen können,
// welche automatisch versendeten Nachrichten (Geburtstag / Jahrestag /
// Neujahr / Willkommen zurück) ein Kunde bekommen hat. Klassifizierung
// per `message.auto_source` (Backend `automated_messages.py`).
const AUTO_KIND_META = {
  birthday:     { label: "Geburtstag",       icon: Cake,        color: "#B02A37" },
  anniversary:  { label: "Jahrestag",        icon: Sparkles,    color: BRAND_COLORS.gold },
  new_year:     { label: "Neujahr",          icon: PartyPopper, color: BRAND_COLORS.navy },
  welcome_back: { label: "Willkommen zurück", icon: Home,        color: "#4B7A55" },
};
const AUTO_KINDS = Object.keys(AUTO_KIND_META);

export default function TeamChat() {
  const { t } = useI18n();
  const { user, isImpersonatingConcierge, activeConciergeId, impersonatedConcierge } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]); // [{ id, name, preview_url, is_image }]
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const fileRef = useRef(null);
  // Feb 2026 · User-Directive (1 Sep 2026) · „Neueste Nachrichten oben":
  // topRef ist der neue Scroll-Anker am Anfang des umgekehrt gerenderten
  // Verlaufs. Der frühere `endRef` (Auto-Scroll nach unten) entfällt.
  const topRef = useRef(null);
  const searchDebounce = useRef(null);
  const messageRefs = useRef({});

  // Feb 2026 · User-Directive „Vereinheitlichte Chip-Leiste" (3 Sep 2026):
  // Ein einziger Radio-State steuert den Thread-Filter UND das Message-
  //   "all"          Alle sichtbar (Default)
  //   "unread"       thr.unread > 0
  //   "attachment"   thr.has_attachment === true
  //   "voice"        thr.has_voice === true
  //   "automated"    thr.has_auto === true (dann optional autoKind-Subset)
  // AutoKind (nur wenn activeFilter === "automated"): birthday | anniversary
  //   | new_year | welcome_back  →  filtert zusätzlich Threads mit
  //   `auto_kinds.includes(kind)` und im Message-Panel nur diese Kind.
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeAutoKind, setActiveAutoKind] = useState(null);
  const setFilter = (key) => {
    setActiveFilter(key);
    if (key !== "automated") setActiveAutoKind(null);
  };

  const filteredThreads = useMemo(() => {
    let base = threads;
    switch (activeFilter) {
      case "unread":     base = base.filter((t) => (t.unread || 0) > 0); break;
      case "attachment": base = base.filter((t) => t.has_attachment);     break;
      case "voice":      base = base.filter((t) => t.has_voice);          break;
      case "automated":
        base = base.filter((t) => t.has_auto);
        if (activeAutoKind) {
          base = base.filter((t) => (t.auto_kinds || []).includes(activeAutoKind));
        }
        break;
      default: break;
    }
    return base;
  }, [threads, activeFilter, activeAutoKind]);
  const unreadTotal = useMemo(() => threads.reduce((s, t) => s + Number(t.unread || 0), 0), [threads]);
  const attachmentTotal = useMemo(() => threads.filter((t) => t.has_attachment).length, [threads]);
  const voiceTotal      = useMemo(() => threads.filter((t) => t.has_voice).length, [threads]);
  const automatedTotal  = useMemo(() => threads.filter((t) => t.has_auto).length, [threads]);

  const loadThreads = useCallback(async () => {
    const scopeId = isImpersonatingConcierge ? (activeConciergeId || impersonatedConcierge?.id || null) : null;
    const url = scopeId ? `/threads?scope_concierge_id=${encodeURIComponent(scopeId)}` : "/threads";
    const { data } = await api.get(url);
    setThreads(data);
    setActive((prev) => prev || data[0] || null);
  }, [isImpersonatingConcierge, activeConciergeId, impersonatedConcierge]);
  const loadMessages = useCallback(async (threadId) => {
    const { data } = await api.get(`/messages/${threadId}?mark_read=true`);
    setMessages(data);
    // Feb 2026 · Badge SOFORT auf 0 setzen (Bell + Chat-Icon-Rotpunkt) —
    // ohne den nächsten 12-s-Poll abzuwarten. Feuert Custom-Event, den
    // jede `useUnreadMessages`-Instanz abhört.
    bumpUnreadMessages();
  }, []);

  useEffect(() => {
    loadThreads();
    const iv = setInterval(loadThreads, 8000);
    return () => clearInterval(iv);
  }, [loadThreads]);
  const [searchParams] = useSearchParams();
  const activeThreadId = active?.thread_id;
  useEffect(() => { if (activeThreadId) loadMessages(activeThreadId); }, [activeThreadId, loadMessages]);

  // 28 Jun 2026 · Deep-Link aus der Telegram-„Chat öffnen"-Nachricht
  // (?thread=<customer_id>): sobald die Thread-Liste geladen ist, genau
  // dieses Gespräch öffnen. Ref-Guard pro Parameterwert, damit der 8-s-Poll
  // die Auswahl nicht überschreibt und ein erneuter Link (anderer Kunde) greift.
  const deepLinkThread = searchParams.get("thread");
  const appliedThreadRef = useRef(null);
  useEffect(() => {
    if (!deepLinkThread || !threads.length) return;
    if (appliedThreadRef.current === deepLinkThread) return;
    const match = threads.find((thr) => thr.thread_id === deepLinkThread)
      || { thread_id: deepLinkThread, customer: null, last_message: null, unread: 0 };
    appliedThreadRef.current = deepLinkThread;
    setActive(match);
  }, [deepLinkThread, threads]);
  // Feb 2026 · User-Directive „Vereinheitlichte Chip-Leiste" (3 Sep 2026):
  // Beim Thread-Wechsel Radio-Filter zuruecksetzen — Kategorie-Chips
  // beziehen sich immer nur auf den aktuell offenen Kunden-Verlauf.
  // Vorher stand hier ein toter `setAutoFilters(new Set())`-Aufruf aus
  // der alten Multi-Select-Version — die State-Variable existiert seit
  // dem Refactor auf Radio-Chips nicht mehr, der Aufruf warf zur
  // Laufzeit `ReferenceError: setAutoFilters is not defined` und liess
  // die TeamChat-Seite crashen.
  useEffect(() => {
    setActiveFilter("all");
    setActiveAutoKind(null);
  }, [active?.thread_id]);

  // Count nach `auto_source` für Chip-Badges. Nur system-Messages, die
  // Feb 2026 · Chip-Zähler-Berechnung — 4 fest definierte AUTO_KINDs.
  // `autoCounts` wird pro geöffnetem Thread live neu berechnet und im
  // vereinheitlichten Auto-Kind-Subchip-Balken angezeigt.
  const autoCounts = useMemo(() => {
    const out = { birthday: 0, anniversary: 0, new_year: 0, welcome_back: 0 };
    for (const m of messages || []) {
      const src = m?.auto_source;
      if (src && out[src] !== undefined) out[src] += 1;
    }
    return out;
  }, [messages]);

  // Feb 2026 · User-Directive „Vereinheitlichte Chip-Leiste" (3 Sep 2026):
  // Message-Panel-Filter zieht jetzt aus demselben State wie die Thread-
  // Chip-Leiste. Alt: `autoFilters`-Set mit Multi-Select. Neu: Radio.
  //   activeFilter === "attachment" → nur Messages mit Anhang
  //   activeFilter === "voice"      → nur Sprachnachrichten
  //   activeFilter === "automated"  → nur auto_source-Messages
  //                                    (+ evtl. activeAutoKind)
  const visibleMessages = useMemo(() => {
    let base = messages || [];
    switch (activeFilter) {
      case "attachment":
        base = base.filter((m) => (m?.attachment_ids?.length || 0) > 0 || !!m?.attachment_id);
        break;
      case "voice":
        base = base.filter((m) => !!m?.voice_transcription || !!m?.voice_url || m?.kind === "voice");
        break;
      case "automated":
        base = base.filter((m) => !!m?.auto_source && (!activeAutoKind || m.auto_source === activeAutoKind));
        break;
      default: break;
    }
    return [...base].reverse();
  }, [messages, activeFilter, activeAutoKind]);
  // Feb 2026 · Neueste-oben-Regel: bei Thread-Wechsel oder neuer Nachricht
  // (Send, Poll) springt der Verlauf zurück nach oben — nicht nach unten.
  // Ausnahme: eine aktive Suchhervorhebung (`highlightId`) hat Vorrang,
  // damit der Klick auf ein Suchergebnis nicht sofort wieder wegscrollt.
  useEffect(() => {
    if (highlightId) return; // don't override search-hit scroll
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [messages.length, highlightId]);

  // Debounced search
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    searchDebounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/messages/search", { params: { q, limit: 60 } });
        setResults(data);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      } finally { setSearching(false); }
    }, 300);
    return () => searchDebounce.current && clearTimeout(searchDebounce.current);
  }, [query]);

  // Scroll & flash the matched message after messages load
  useEffect(() => {
    if (!highlightId) return;
    const el = messageRefs.current[highlightId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => setHighlightId(null), 2200);
      return () => clearTimeout(t);
    }
  }, [messages, highlightId]);

  const openSearchResult = async (r) => {
    // Find existing thread entry or synthesize one from result
    const existing = threads.find((t) => t.thread_id === r.thread_id);
    const threadObj = existing || { thread_id: r.thread_id, customer: r.customer, last_message: null, unread: 0 };
    setHighlightId(r.message_id);
    setActive(threadObj);
    setQuery("");
    setResults([]);
  };

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (files.length === 0 || !active) return;
    const free = MAX_ATTACHMENTS - pending.length;
    if (free <= 0) { toast.error(`Maximal ${MAX_ATTACHMENTS} Anhänge pro Nachricht.`); return; }
    const toUpload = files.slice(0, free);
    if (files.length > free) toast.info(`Nur ${free} weitere Datei(en) werden hochgeladen (max. ${MAX_ATTACHMENTS} pro Nachricht).`);
    setUploading(true);
    try {
      for (const file of toUpload) {
        const isImage = file.type.startsWith("image/");
        const localPreview = isImage ? URL.createObjectURL(file) : null;
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data: fileRefResp } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setPending((p) => [...p, { id: fileRefResp.id, name: file.name, preview_url: localPreview, is_image: isImage }]);
        } catch (err) {
          if (localPreview) URL.revokeObjectURL(localPreview);
          toast.error(`${file.name}: ${formatErr(err.response?.data?.detail) || err.message}`);
        }
      }
    } finally { setUploading(false); }
  };

  const removePending = (id) => {
    setPending((p) => {
      const target = p.find((x) => x.id === id);
      if (target?.preview_url) URL.revokeObjectURL(target.preview_url);
      return p.filter((x) => x.id !== id);
    });
  };

  const send = async (e) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!active) return;
    if (!trimmed && pending.length === 0) return;
    setSending(true);
    try {
      const payload = { thread_id: active.thread_id };
      if (trimmed) payload.text = trimmed;
      if (pending.length > 0) payload.attachment_ids = pending.map((p) => p.id);
      const { data: msg } = await api.post("/messages", payload);
      setText("");
      pending.forEach((p) => { if (p.preview_url) URL.revokeObjectURL(p.preview_url); });
      setPending([]);
      setMessages((m) => [...m, msg]);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const canAddMore = pending.length < MAX_ATTACHMENTS;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-chat">
      <div className="mb-8">
        <div className="label-caps mb-3">Weissblom Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">{t.team.chat_inbox_title}</h1>
      </div>

      <div className="mb-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-dark/40" strokeWidth={1.6} />
        <input
          data-testid="team-chat-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suche in allen Nachrichten (min. 2 Zeichen) …"
          className="w-full bg-white border border-navy/15 focus:border-gold focus:outline-none pl-9 pr-10 py-3 text-[13px] text-navy-dark"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); setResults([]); }}
            data-testid="team-chat-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-dark/50 hover:text-navy"
            aria-label="Suche zurücksetzen"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        )}
      </div>

      {query.trim().length >= 2 && (
        <div className="mb-6 bg-white border border-navy/10" data-testid="team-chat-search-results">
          <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
              {searching ? "Suche …" : `${results.length} Treffer für „${query.trim()}"`}
            </div>
          </div>
          <div className="max-h-[40vh] overflow-y-auto">
            {results.map((r) => (
              <button
                key={r.message_id}
                data-testid={`team-chat-search-result-${r.message_id}`}
                onClick={() => openSearchResult(r)}
                className="w-full text-left px-5 py-4 border-b border-navy/5 hover:bg-ivory/60 transition-colors"
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-navy font-medium truncate text-[14px]">
                      {r.customer?.name || r.customer?.email || "Kunde"}
                    </div>
                    {r.matched_in_voice && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-navy-dark/60">
                        <Mic className="w-3 h-3" strokeWidth={1.6} /> Sprachnachricht
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-navy-dark/50 shrink-0">
                    {r.created_at ? deDateTime(r.created_at) : ""}
                  </div>
                </div>
                <div className="text-[13px] text-navy-dark/80 leading-relaxed">
                  {r.snippet}
                </div>
              </button>
            ))}
            {!searching && results.length === 0 && (
              <div className="px-5 py-6 text-[13px] text-navy-dark/50 italic">Keine Treffer.</div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[70vh]">
        <div className="bg-white border border-navy/10 overflow-y-auto" data-testid="team-thread-list">
          {/* Feb 2026 · User-Directive „Vereinheitlichte Chip-Leiste"
              (3 Sep 2026): Ein einziger Radio-Filter für Threads UND
              Message-Panel. Chips: Alle · Ungelesen · Mit Anhang ·
              Sprachnachrichten · Automatisiert. Bei „Automatisiert" öffnet
              sich eine zweite Zeile mit den granularen AUTO_KINDs. */}
          <div
            className="flex items-center gap-1 p-2 border-b border-navy/10 flex-wrap"
            role="tablist"
            aria-label="Nachrichten filtern"
            data-testid="team-chat-filter-bar"
          >
            {[
              { key: "all",        label: "Alle",             icon: null,       count: threads.length },
              { key: "unread",     label: "Ungelesen",        icon: null,       count: unreadTotal },
              { key: "attachment", label: "Mit Anhang",       icon: Paperclip,  count: attachmentTotal },
              { key: "voice",      label: "Sprachnachrichten", icon: Mic,        count: voiceTotal },
              { key: "automated",  label: "Automatisiert",    icon: Bot,        count: automatedTotal },
            ].map((c) => {
              const active = activeFilter === c.key;
              const Icon = c.icon;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`team-chat-filter-${c.key}`}
                  onClick={() => setFilter(c.key)}
                  className={
                    "inline-flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] tracking-[0.14em] uppercase border transition-colors " +
                    (active ? "bg-navy text-ivory border-navy" : "bg-white text-navy border-navy/25 hover:bg-navy/5")
                  }
                >
                  {Icon && <Icon className="w-3 h-3" strokeWidth={2} aria-hidden />}
                  <span>{c.label}</span>
                  <span
                    className={
                      "tabular-nums text-[10px] px-1.5 py-0.5 rounded-sm normal-case tracking-normal " +
                      (active ? "bg-white/20" : "bg-navy/5")
                    }
                    data-testid={`team-chat-filter-${c.key}-count`}
                  >
                    {c.count}
                  </span>
                </button>
              );
            })}
          </div>
          {activeFilter === "automated" && (
            <div
              className="flex items-center gap-1 px-2 py-2 border-b border-navy/10 bg-ivory/40 flex-wrap"
              data-testid="team-chat-auto-kind-bar"
            >
              <span className="text-[9.5px] tracking-[0.14em] uppercase text-navy-dark/55 mr-1">
                Kategorie
              </span>
              {AUTO_KINDS.map((kind) => {
                const meta = AUTO_KIND_META[kind];
                const active = activeAutoKind === kind;
                const Icon = meta.icon;
                return (
                  <button
                    type="button"
                    key={kind}
                    onClick={() => setActiveAutoKind(active ? null : kind)}
                    data-testid={`team-chat-auto-kind-${kind}`}
                    data-active={active ? "true" : "false"}
                    className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.14em] uppercase rounded-full transition-colors"
                    style={active
                      ? { background: meta.color, color: BRAND_COLORS.ivory, border: `1px solid ${meta.color}` }
                      : { background: "white", color: BRAND_COLORS.navy, border: `1px solid ${withAlpha(BRAND_COLORS.navy, 0.15)}` }}
                  >
                    <Icon className="w-3 h-3" strokeWidth={1.8} />
                    {meta.label}
                  </button>
                );
              })}
              {activeAutoKind && (
                <button
                  type="button"
                  onClick={() => setActiveAutoKind(null)}
                  data-testid="team-chat-auto-kind-clear"
                  className="text-[10px] tracking-[0.14em] uppercase text-navy-dark/60 hover:text-navy ml-1"
                >
                  Alle Kategorien
                </button>
              )}
            </div>
          )}
          {filteredThreads.map((thr) => {
            const isUnread = (thr.unread || 0) > 0;
            return (
            <button
              key={thr.thread_id}
              data-testid={`team-thread-${thr.thread_id}`}
              data-unread={isUnread ? "1" : "0"}
              onClick={() => setActive(thr)}
              className={`w-full text-left px-5 py-4 border-b border-navy/5 transition-colors ${active?.thread_id === thr.thread_id ? "bg-ivory" : "hover:bg-ivory/60"}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Feb 2026 · User-Directive „Ungelesen deutlich markiert":
                      Ungelesene Threads bekommen fetten Titel + goldenen
                      Punkt (Voranzeige) + den bestehenden roten Zähler-
                      Badge (rechts). */}
                  {isUnread && (
                    <span
                      data-testid={`unread-marker-${thr.customer?.id}`}
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: BRAND_COLORS.gold }}
                      aria-hidden
                    />
                  )}
                  <div className={`truncate ${isUnread ? "text-navy font-bold" : "text-navy font-medium"}`}>
                    {thr.customer?.name || thr.customer?.email}
                  </div>
                  {thr.unread > 0 && (
                    <span
                      data-testid={`unread-dot-${thr.customer?.id}`}
                      title={`${thr.unread} ungelesen`}
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-medium leading-none"
                      style={{ background: "#C0392B" }}
                    >
                      {thr.unread}
                    </span>
                  )}
                  {/* Feb 2026 · Kleine Signal-Icons in der Thread-Zeile —
                      zeigen auf einen Blick, welche Chip-Kategorien in
                      diesem Verlauf mindestens einmal vorkommen. */}
                  {thr.has_attachment && (
                    <Paperclip
                      className="w-3 h-3 shrink-0"
                      strokeWidth={1.8}
                      style={{ color: BRAND_COLORS.navy, opacity: 0.55 }}
                      data-testid={`thread-signal-attachment-${thr.customer?.id}`}
                      aria-label="Mit Anhang"
                    />
                  )}
                  {thr.has_voice && (
                    <Mic
                      className="w-3 h-3 shrink-0"
                      strokeWidth={1.8}
                      style={{ color: BRAND_COLORS.navy, opacity: 0.55 }}
                      data-testid={`thread-signal-voice-${thr.customer?.id}`}
                      aria-label="Sprachnachricht"
                    />
                  )}
                  {thr.has_auto && (
                    <Bot
                      className="w-3 h-3 shrink-0"
                      strokeWidth={1.8}
                      style={{ color: BRAND_COLORS.gold, opacity: 0.85 }}
                      data-testid={`thread-signal-auto-${thr.customer?.id}`}
                      aria-label="Automatisiert"
                    />
                  )}
                </div>
              </div>
              <div className={`text-[12px] truncate mt-1 ${isUnread ? "text-navy font-semibold" : "text-navy-dark/60"}`}>
                {thr.last_message?.text || ((thr.last_message?.attachment_ids && thr.last_message.attachment_ids.length) || thr.last_message?.attachment_id ? "[Anhang]" : "—")}
              </div>
            </button>
          );})}
          {threads.length === 0 && <div className="p-6 text-[13px] text-navy-dark/50">—</div>}
          {threads.length > 0 && filteredThreads.length === 0 && (
            <div
              className="p-6 text-[13px] text-navy-dark/55 italic"
              data-testid="team-chat-empty-filter"
            >
              Keine Treffer. Klicken Sie „Alle", um wieder alle Konversationen zu sehen.
            </div>
          )}
        </div>

        <div className="md:col-span-2 bg-white border border-navy/10 flex flex-col">
          {active ? (
            <>
              <div className="border-b border-navy/10 px-6 py-4">
                <div className="label-caps">Konversation</div>
                <div className="font-display text-xl text-navy">{active.customer?.name || active.customer?.email}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="team-chat-messages">
                <div ref={topRef} data-testid="team-chat-messages-top" />
                {visibleMessages.length === 0 && activeFilter !== "all" && (
                  <div
                    className="text-[13px] text-navy-dark/55 italic text-center py-8"
                    data-testid="team-chat-empty-filtered"
                  >
                    Keine Nachricht im aktiven Filter.
                  </div>
                )}
                {visibleMessages.map((m) => {
                  const mine = m.sender_id === user.id;
                  const isSystem = m.sender_role === "system";
                  const isFamily = m.sender_role === "family";
                  const atts = m.attachment_ids && m.attachment_ids.length ? m.attachment_ids : (m.attachment_id ? [m.attachment_id] : []);
                  const isHit = highlightId === m.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} ref={(el) => { if (el) messageRefs.current[m.id] = el; }}>
                      <div className="max-w-[70%]">
                        <div className={`label-caps mb-1 flex items-center gap-1.5 ${mine ? "justify-end" : ""}`}>
                          <span>{mine ? "Weissblom" : (m.sender_name || "Kunde")}</span>
                          {isFamily && (
                            <span
                              className="text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 border"
                              style={{ background: "#EDE3C8", color: "#8A7635", borderColor: BRAND_COLORS.gold }}
                              data-testid={`team-chat-family-badge-${m.id}`}
                              title="Familienmitglied — Read-only-Rechte auf die meisten Funktionen"
                            >
                              Familie
                            </span>
                          )}
                        </div>
                        {isSystem ? (
                          m.meta?.kind === "preferences_diff" ? (
                            <div data-testid={`team-chat-system-prefs-diff-${m.id}`}>
                              <PreferencesDiffCard
                                diff={m.meta.diff}
                                snapshot={m.meta.snapshot}
                                testidBase={`team-chat-prefs-diff-${m.id}`}
                              />
                            </div>
                          ) : (
                            <div
                              className="px-4 py-2.5 border text-[12.5px] text-navy-dark/85 italic leading-relaxed"
                              style={{ background: "#F5EEDB", borderColor: BRAND_COLORS.gold }}
                              data-testid={`team-chat-system-msg-${m.id}`}
                              data-auto-source={m.auto_source || ""}
                            >
                              {m.auto_source && AUTO_KIND_META[m.auto_source] && (() => {
                                const meta = AUTO_KIND_META[m.auto_source];
                                const Icon = meta.icon;
                                return (
                                  <div
                                    className="mb-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.16em] uppercase rounded-sm not-italic"
                                    style={{ background: meta.color, color: BRAND_COLORS.ivory }}
                                    data-testid={`team-chat-auto-badge-${m.id}`}
                                  >
                                    <Icon className="w-2.5 h-2.5" strokeWidth={2} />
                                    {meta.label}
                                  </div>
                                );
                              })()}
                              {m.auto_source && AUTO_KIND_META[m.auto_source] && <br />}
                              {m.text}
                            </div>
                          )
                        ) : (
                          <div
                            className={`px-4 py-3 border transition-all duration-500 ${mine ? "border-navy" : "border-ivory-dark"} ${isHit ? "ring-2 ring-offset-2" : ""}`}
                            style={{
                              ...(mine ? { background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory } : { background: "#fff", color: BRAND_COLORS.navyDark }),
                              ...(isHit ? { boxShadow: "0 0 0 3px #C9A961" } : {}),
                            }}
                          >
                            {m.text && <div className="text-[14px] leading-relaxed whitespace-pre-wrap">{m.text}</div>}
                            {atts.length > 0 && (
                              <div className={`${m.text ? "mt-2" : ""} space-y-2`}>
                                {atts.map((fid) => (
                                  <ChatAttachment
                                    key={fid}
                                    fileId={fid}
                                    mine={mine}
                                    transcription={m.voice_transcription}
                                    messageId={m.id}
                                    editable={true}
                                    onTranscriptionUpdate={(tr) => setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, voice_transcription: tr } : x))}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pending attachments preview grid */}
              {pending.length > 0 && (
                <div className="border-t border-navy/10 px-4 py-3 bg-ivory/60" data-testid="team-pending-attachments">
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
                      {pending.length} von {MAX_ATTACHMENTS} bereit
                    </div>
                    <div className="text-[11px] text-navy-dark/50 italic">Wird mit „Senden" abgeschickt.</div>
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    {pending.map((p) => (
                      <div key={p.id} className="relative border border-navy/15 bg-white" data-testid={`team-pending-item-${p.id}`}>
                        {p.is_image ? (
                          <img src={p.preview_url} alt={p.name} className="w-20 h-20 object-cover" />
                        ) : (
                          <div className="w-20 h-20 flex flex-col items-center justify-center gap-1 px-2 text-center">
                            <FileText className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
                            <div className="text-[9px] text-navy-dark/70 leading-tight truncate w-full" title={p.name}>{p.name}</div>
                          </div>
                        )}
                        <button
                          type="button"
                          data-testid={`team-pending-remove-${p.id}`}
                          onClick={() => removePending(p.id)}
                          className="absolute -top-2 -right-2 bg-white text-navy border border-navy/30 hover:border-navy p-0.5 shadow-sm"
                          aria-label="Anhang entfernen"
                        >
                          <X className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={send} className="border-t border-navy/10 p-4 flex items-center gap-3">
                <input
                  ref={fileRef}
                  data-testid="team-chat-file-input"
                  type="file"
                  accept={ACCEPT}
                  multiple
                  onChange={pickFiles}
                  className="hidden"
                />
                <button
                  type="button"
                  data-testid="team-chat-attach-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading || sending || !canAddMore}
                  title={!canAddMore ? `Maximal ${MAX_ATTACHMENTS} Anhänge` : ""}
                  className="border border-navy/20 text-navy px-3 py-2.5 text-[12px] tracking-wider uppercase hover:border-gold transition-colors disabled:opacity-50"
                >
                  {uploading ? "…" : t.chat.attach}
                </button>
                <input
                  data-testid="team-chat-text-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={pending.length > 0 ? "Nachricht zum Anhang (optional) …" : t.chat.placeholder}
                  className="flex-1 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark"
                />
                <button
                  data-testid="team-chat-send-btn"
                  type="submit"
                  disabled={sending || (!text.trim() && pending.length === 0)}
                  className="bg-navy text-ivory px-6 py-2.5 text-[12px] tracking-wider uppercase hover:bg-navy-dark transition-colors disabled:opacity-40"
                  style={{ background: BRAND_COLORS.navy }}
                >
                  {sending ? "…" : t.chat.send}
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[13px] text-navy-dark/50">Wählen Sie eine Konversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
